import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// material-ui
import { 
    Button, Grid, Typography, 
    IconButton, Divider, Box,
    Card, CardContent, CardHeader, CircularProgress,
    Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Stack
} from '@mui/material'
import { 
    ArrowBack as ArrowBackIcon, 
    Edit as EditIcon,
    Print as PrintIcon
} from '@mui/icons-material'
import { useApiData } from 'services/hook/useApiData'
import DirhamIcon from '../../assets/images/Dirham-Symbol.png'
import DirhamIconColor from '../../assets/images/Dirham-Symbol-color.png'
// project import
import AnimateButton from 'components/@extended/AnimateButton'
import { useSnackbar } from 'notistack'
import { orderDetail } from '../../models/order'
import { DeleteFilled } from '@ant-design/icons'
import { getCurrencyData } from '../../utils/helpers'
import { OrderDeleteDialog } from './Order.Delete'
import { EVENT_ORDER_DELETE_INIT } from '../../models/events/order.event'
import { useEventBusContext } from 'services/provider/EventBusProvider';


const OrderDetail = () => {
    const { orderId } = useParams()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const eventbus = useEventBusContext()
    

    const [data, , loading, loadData] = useApiData({ data: {} }, {}, (params, setData) => {
        orderDetail(Number(orderId)).then((res) => {
            setData(res.data)
            console.log(res.data)
        })
        .catch((res) => {
            if(res?.status_code === 401) {
                signOut();
            }
            setData({ status: false, message: res?.message });
        })
    })

    // Calculate item total
    const calculateItemTotal = (item) => {
        const quantity = parseFloat(item.quantity) || 0
        const unitPrice = parseFloat(item.unit_price) || 0
        const subtotal = quantity * unitPrice
        
        let discountAmount = 0
        if (item.discount_value) {
            if (item.discount_type === 'percentage') {
                discountAmount = (subtotal * parseFloat(item.discount_value)) / 100
            } else {
                discountAmount = parseFloat(item.discount_value)
            }
        }
        
        return {
            subtotal: subtotal.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            total: (subtotal - discountAmount).toFixed(2)
        }
    }

    // Calculate order totals
    const calculateOrderTotals = () => {
        if (!data?.order_items || data.order_items.length === 0) {
            return {
                totalQuantity: 0,
                netTotal: '0.00',
                taxAmount: '0.00',
                grandTotal: '0.00'
            }
        }

        let totalQuantity = 0
        let netTotal = 0
        
        data.order_items.forEach(item => {
            totalQuantity += parseFloat(item.quantity) || 0
            const itemCalc = calculateItemTotal(item)
            netTotal += parseFloat(itemCalc.total)
        })
        
        const taxRate = parseFloat(data.tax_rate) || 0
        const taxAmount = (netTotal * taxRate) / 100
        const grandTotal = netTotal + taxAmount
        
        return {
            totalQuantity,
            netTotal: netTotal.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            grandTotal: grandTotal.toFixed(2)
        }
    }

    const getStatusColor = (status) => {
        const statusColors = {
            pending: 'warning',
            confirmed: 'info',
            processing: 'primary',
            delivered: 'success',
            cancelled: 'error'
        }
        return statusColors[status] || 'default'
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress size={60} />
            </Box>
        )
    }
    const CurrencyWithIcon = ({ amount, color = false, align='start', ...props }) => {
        const currencyData = getCurrencyData(amount);
        
        return (
            <Stack direction="row" spacing={0.5} alignItems='center' sx={{justifyContent: align}} {...props}>
                <img
                    src={color ? DirhamIconColor : DirhamIcon}
                    width={20}
                    alt="AED"
                />
                <span>{currencyData.amount}</span>
            </Stack>
        );
    };

    const totals = calculateOrderTotals()

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, sm: 3, md: 4 } }}>
            <OrderDeleteDialog />
            <Grid container spacing={3}>
                <Grid item size={{ xs: 12 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 1
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <IconButton 
                                onClick={() => navigate('/orders')} 
                                color="primary"
                                size="large"
                                sx={{ 
                                    bgcolor: 'primary.lighter',
                                    '&:hover': { bgcolor: 'primary.light' }
                                }}
                            >
                                <ArrowBackIcon fontSize="medium" />
                            </IconButton>
                            <Typography variant="h3">
                                Order #{data.order_number}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <AnimateButton>
                                <Button
                                    variant="contained"
                                    startIcon={<DeleteFilled />}
                                    onClick={() => eventbus.fire(EVENT_ORDER_DELETE_INIT, data)}
                                    color="error"
                                    size="large"
                                    sx={{ fontSize: '1rem' }}
                                >
                                    Delete Order
                                </Button>
                            </AnimateButton>
                        </Stack>
                    </Box>
                </Grid>

                {/* Order Information - Redesigned with Grid Layout */}
                <Grid item size={{ xs: 12 }}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 3,
                        }}
                    >
                        <CardHeader 
                            title={<Typography variant="h4" sx={{ fontWeight: 600}}>Order Information</Typography>}
                            sx={{ pb: 2 }}
                        />
                        <CardContent>
                            <Grid container spacing={3}>
                                {/* Customer Info */}
                                <Grid item size={{ md:3, sm:6, xs:12 }} >
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Customer
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                            {data.customer?.name || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Status */}
                                <Grid item size={{ md:3, sm:6, xs:12 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Status
                                        </Typography>
                                        <Chip 
                                            label={data.status || 'UNKNOWN'} 
                                            color={getStatusColor(data.status)}
                                            size="medium"
                                            sx={{ fontWeight: 600, fontSize: '16px', textTransform: 'capitalize', }}
                                        />
                                    </Box>
                                </Grid>

                                {/* Order Date */}
                                <Grid item size={{ md:3, sm:6, xs:12 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Order Date
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                            {data.created_at ? new Date(data.created_at).toLocaleDateString() : 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Delivery Date */}
                                <Grid item size={{ md:3, sm:6, xs:12 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Delivery Date
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                            {data.delivery_date ? new Date(data.created_at).toLocaleDateString('en-US', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                                }) : 'Not set'}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Preparation Time */}
                                <Grid item size={{ md:3, sm:6, xs:12 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Preferred Time of Delivery
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                          {data.preparationtime?.time
                                            ? `${data.preparationtime.time}${data.preparationtime?.label ? ` (${data.preparationtime.label})` : ''}`
                                            : 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>                               

                                <Grid item size={{ xs:12 }}>
                                    <Divider />
                                </Grid>                                

                                {/* Order Summary in Grid */}
                                <Grid item size={{ md:3, sm:4, xs:6 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Total Items
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                                            {data.items?.length || 0}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item size={{ md:3, sm:4, xs:6 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Subtotal
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                                            <CurrencyWithIcon amount={data.subtotal || 0} />
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item size={{ md:3, sm:4, xs:6 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Discount
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                                            <CurrencyWithIcon amount={data.discount_amount || 0} />
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item size={{ md:3, sm:4, xs:6 }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary" 
                                            sx={{ 
                                                textTransform: 'uppercase', 
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}
                                        >
                                            Grand Total
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'success.main' }}>
                                            <CurrencyWithIcon amount={data.total || 0} color="true" />
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>                        
                    </Card>
                    <Card sx={{ mt:3}}>
                        <CardContent sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                             <Grid item size={{ md:12, sm:12, xs:12 }}>
                                <Box>
                                    <Typography 
                                        variant="caption" 
                                        color="text.secondary" 
                                        sx={{ 
                                            textTransform: 'uppercase', 
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            display: 'block',
                                            mb: 0.5
                                        }}
                                    >
                                        Notes
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                        {data.notes || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Order Items Table */}
                <Grid item size={{ xs: 12 }}>
                    <Card 
                        elevation={0}
                    >
                        <CardHeader sx={{px: 0, py: 2}}
                            title={<Typography variant="h4" sx={{ fontWeight: 600, fontSize: '1.5rem', p:0 }}>Order Items</Typography>}
                        />
                        <CardContent sx={{p:0}}>
                            <TableContainer 
                                component={Paper} 
                                variant="outlined"
                                sx={{ 
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ py: 2.5, fontSize: '14px', color: 'text.secondary' }}>S.No</TableCell>
                                            <TableCell sx={{ py: 2.5, fontSize: '14px', color: 'text.secondary' }}>Product</TableCell>
                                            <TableCell align="right" sx={{ py: 2.5, fontSize: '14px', color: 'text.secondary' }}>Quantity</TableCell>
                                            <TableCell align="right" sx={{ py: 2.5, fontSize: '14px', color: 'text.secondary' }}>Unit Price</TableCell>
                                            <TableCell align="right" sx={{ py: 2.5, fontSize: '14px', color: 'text.secondary' }}>Discount</TableCell>
                                            <TableCell align="right" sx={{ py: 2.5, fontSize: '14px', color: 'text.secondary' }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.items?.map((item, index) => {
                                            const itemCalc = calculateItemTotal(item)
                                            return (
                                                <TableRow 
                                                    key={index} 
                                                    hover
                                                    sx={{ 
                                                        '&:last-child td': { border: 0 },
                                                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' }
                                                    }}
                                                >
                                                    <TableCell sx={{ fontSize: '1rem', py: 3 }}>
                                                        <Box sx={{ 
                                                            width: 36, 
                                                            height: 36, 
                                                            borderRadius: '50%', 
                                                            bgcolor: 'primary.main',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontWeight: 700,
                                                            color: 'white',
                                                            fontSize: '1rem'
                                                        }}>
                                                            {index + 1}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                                            {item.product_name || item.product?.name || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                                            {item.quantity}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                                                             <CurrencyWithIcon amount={item.product.regular_price || 0} align='end' />
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body1" sx={{ fontSize: '1rem', color: 'error.main', fontWeight: 500 }}> 
                                                            <CurrencyWithIcon amount={item.product?.discount_amount || 0} align='end'/>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: 'success.main' }}>
                                                            <CurrencyWithIcon amount={item.total || 0} color="true" align='end' />
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                        {(!data.items || data.items.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                                                        No items in this order
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
                
            </Grid>
        </Box>
    )
}

export default OrderDetail